import { PlotReportFactElement, PlotReportFactLabelFilter, R1FactType } from '@/api/rest/resources/types/fact';
import { Plot } from '@/api/rest/resources/types/plot';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GRAPH_TYPE, GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import { NCCardAnalysisType, useNCCardContext } from '@/components/NCCard/NCCard';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { usePlotReportForPlots } from '@/pages/shared/hooks/usePlotReportForPlot';
import { hasLabel } from '@/pages/shared/hooks/utils';

import { ItemizedPlot } from './usePlotsForItemizedGraph';

type UseGraphFactsToPlotsMapProps = {
  aggregationPlots: ItemizedPlot[];
  indicator: GraphTileType;
  graphType: GraphTypeValue;
  plotGroupId?: string;
  labels?: PlotReportFactLabelFilter;
};

export const useIndicatorFactsMap = (): Partial<
  Record<GraphTypeValue, Partial<Record<GraphTileType, Partial<Record<NCCardAnalysisType, R1FactType | null>>>>>
> => {
  const aboveGroundCarbonFacts = useAboveGroundCarbonFacts();

  return {
    [GRAPH_TYPE.LINE]: {
      [GraphTileType.historic_below_ground_carbon]: {
        total: R1FactType.r1_soil_carbon_historic_total_graph,
        average: R1FactType.r1_soil_carbon_historic_per_ha_graph,
      },
      [GraphTileType.historic_above_ground_carbon]: {
        total: aboveGroundCarbonFacts?.total.graph,
        average: aboveGroundCarbonFacts?.perHa.graph,
      },
      [GraphTileType.historic_water_soil_moisture]: {
        total: R1FactType.r1_historic_water_and_soil_moisture_graph,
        average: R1FactType.r1_historic_water_and_soil_moisture_per_ha_graph,
      },
      [GraphTileType.protected_on_farm_habitat]: {
        total: R1FactType.r1_biodiversity_zone_percent_historic_graph,
        average: null,
      },
      [GraphTileType.social_life_expectancy]: {
        total: R1FactType.social_life_expectancy_historic_graph,
        average: null,
      },
      [GraphTileType.ndvi]: {
        total: R1FactType.vegetation_condition_ndvi_historic_graph,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness]: {
        total: R1FactType.habitat_intactness_total_historic_graph,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness_threats_breakdown]: {
        total: R1FactType.threat_breakdown_total_historic_graph,
        average: null,
      },
      [GraphTileType.historic_deforestation]: {
        total: R1FactType.deforestation_total_historic_graph,
        average: null,
      },
    },
    [GRAPH_TYPE.BAR]: {
      [GraphTileType.historic_below_ground_carbon]: {
        total: R1FactType.r1_carbon_bg_latest_analysis_total,
        average: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
      },
      [GraphTileType.historic_above_ground_carbon]: {
        total: R1FactType.above_ground_carbon_total,
        average: R1FactType.above_ground_carbon_per_ha,
      },
      [GraphTileType.historic_water_soil_moisture]: {
        total: R1FactType.r1_water_holding_capacity_latest_analysis_total,
        average: R1FactType.r1_water_holding_capacity_latest_analysis_per_ha,
      },
      [GraphTileType.protected_on_farm_habitat]: {
        total: R1FactType.r1_biodiversity_latest_analysis,
        average: null,
      },
      [GraphTileType.social_life_expectancy]: {
        total: R1FactType.social_life_expectancy,
        average: null,
      },
      [GraphTileType.ndvi]: {
        total: R1FactType.vegetation_condition_ndvi,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness]: {
        total: R1FactType.habitat_intactness_total,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness_threats_breakdown]: {
        total: R1FactType.threat_breakdown_total,
        average: null,
      },
      [GraphTileType.historic_deforestation]: {
        total: R1FactType.deforestation_total,
        average: null,
      },
    },
  };
};

export type GraphFactsToPlotsMap = {
  plot: ItemizedPlot;
  facts: PlotReportFactElement[];
}[];

export const useGraphFactsToPlotsMap = ({
  aggregationPlots,
  indicator,
  graphType,
  plotGroupId,
  labels,
}: UseGraphFactsToPlotsMapProps): GraphFactsToPlotsMap => {
  const factType = useFactTypeForItemizedGraph({ indicator, graphType });

  const plotReportsData = usePlotReportForPlots(aggregationPlots as Plot[], {
    plot_group_id: plotGroupId ?? undefined,
    fact_names: factType ?? undefined,
    ...labels,
  });

  return aggregationPlots.map((plot, index) => {
    const facts = plotReportsData[`${index}`]?.data?.facts ?? [];

    let filteredFacts = facts as PlotReportFactElement[];

    if (!labels || !factType || filteredFacts.length === 0) {
      return {
        plot,
        facts: filteredFacts,
      };
    }

    // We need to filter the facts by their labels because the backend is ignoring the label filter
    const isGraphFact = factType.includes('graph');

    filteredFacts = isGraphFact
      ? filteredFacts.map((f) => {
          if (!Array.isArray(f.value)) {
            return f;
          }

          const filteredValues = f.value.filter((value) => hasLabel(labels, value.labels ?? []));

          return {
            ...f,
            value: filteredValues,
          };
        })
      : filteredFacts.filter((f) => hasLabel(labels, f.labels ?? []));

    return {
      plot,
      facts: filteredFacts,
    };
  });
};

export const useFactTypeForItemizedGraph = ({
  indicator,
  graphType,
}: {
  indicator: GraphTileType;
  graphType: GraphTypeValue;
}): R1FactType | null => {
  const { analysisType } = useNCCardContext();
  const indicatorFactsMap = useIndicatorFactsMap();
  return indicatorFactsMap[`${graphType}`]?.[`${indicator}`]?.[`${analysisType}`] ?? null;
};
